import { PublicKey } from "@solomon-labs/publickey";
import type { SolanaAddress } from "@solomon-labs/types";
import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from "react";

import type { WalletState } from "../types";

import { readBrowserOverrides } from "./overrides";
import { createSmokeSigner, type SmokeSigner } from "./signer";
import type { SolomonE2EWalletOverride } from "./types";

function normalizeWalletOverride(
    wallet: SolomonE2EWalletOverride | undefined
): SolomonE2EWalletOverride | null {
    if (!wallet) {
        return null;
    }

    return {
        connected: wallet.connected,
        connecting: wallet.connecting ?? false,
        isWalletReady: wallet.isWalletReady ?? wallet.connected,
        address: wallet.address,
        walletName: wallet.walletName ?? null,
        walletIcon: wallet.walletIcon ?? null
    };
}

function parsePublicKey(address: string | null): PublicKey | null {
    if (!address) {
        return null;
    }

    try {
        return new PublicKey(address as SolanaAddress);
    } catch (error) {
        console.error("Failed to parse E2E wallet address:", error);
        return null;
    }
}

const E2EWalletContext = createContext<WalletState | null>(null);
const E2ESignerContext = createContext<SmokeSigner | null>(null);

export function SolomonE2EWalletProvider({
    children
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const [walletOverride, setWalletOverride] =
        useState<SolomonE2EWalletOverride | null>(() =>
            normalizeWalletOverride(readBrowserOverrides()?.wallet)
        );

    const updateWalletOverride = useCallback(
        (nextWallet: SolomonE2EWalletOverride | null): void => {
            if (typeof window !== "undefined") {
                const current = window.__SOLOMON_E2E__ ?? {};
                if (nextWallet) {
                    window.__SOLOMON_E2E__ = {
                        ...current,
                        wallet: nextWallet
                    };
                } else {
                    // Preserve any non-wallet overrides (chain, signer) on
                    // disconnect; only drop the global entirely when nothing
                    // else remains.
                    const rest = { ...current };
                    delete rest.wallet;
                    if (Object.keys(rest).length > 0) {
                        window.__SOLOMON_E2E__ = rest;
                    } else {
                        delete window.__SOLOMON_E2E__;
                    }
                }
            }

            setWalletOverride(nextWallet);
        },
        []
    );

    const value = useMemo((): WalletState | null => {
        if (!walletOverride) {
            return null;
        }

        return {
            connected: walletOverride.connected,
            connecting: walletOverride.connecting ?? false,
            isWalletReady:
                walletOverride.isWalletReady ?? walletOverride.connected,
            publicKey: parsePublicKey(walletOverride.address),
            walletName: walletOverride.walletName ?? null,
            walletIcon: walletOverride.walletIcon ?? null,
            disconnect: async (): Promise<void> => {
                updateWalletOverride({
                    ...walletOverride,
                    connected: false,
                    connecting: false,
                    isWalletReady: false,
                    address: null,
                    walletName: null,
                    walletIcon: null
                });
            }
        };
    }, [updateWalletOverride, walletOverride]);

    // Synthesize the in-memory smoke signer when the signer override is
    // present. Synthesis is synchronous (decode + wrap; the real Ed25519
    // work happens later in `signMessage`), so the signer is available on
    // first render. Keyed on the base58 fields so a stable `window` object
    // doesn't re-create it every render. In production the override is never
    // set, so `createSmokeSigner` is never invoked.
    const signerOverride = readBrowserOverrides()?.signer ?? null;
    const publicKeyBase58 = signerOverride?.publicKeyBase58 ?? null;
    const secretKeyBase58 = signerOverride?.secretKeyBase58 ?? null;
    const signer = useMemo<SmokeSigner | null>(
        () =>
            publicKeyBase58 && secretKeyBase58
                ? createSmokeSigner({ publicKeyBase58, secretKeyBase58 })
                : null,
        [publicKeyBase58, secretKeyBase58]
    );

    return (
        <E2EWalletContext.Provider value={value}>
            <E2ESignerContext.Provider value={signer}>
                {children}
            </E2ESignerContext.Provider>
        </E2EWalletContext.Provider>
    );
}

export function useSolomonE2EWallet(): WalletState | null {
    return useContext(E2EWalletContext);
}

export function useSolomonE2ESigner(): SmokeSigner | null {
    return useContext(E2ESignerContext);
}
